<script setup lang="ts">
import { computed } from 'vue'

import BetaBaseImage from '@/components/beta-editor/BetaBaseImage.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorPageStore } from '@/stores/editor/page'

const MAX_CANVAS_ITEMS = 3

const props = defineProps<{
    element: any
    sectionIndex: number
}>()

const betaStore = useEditorBetaStore()
const editorPageStore = useEditorPageStore()

const posts = computed(() => Array.isArray(props.element?.frameContent) ? props.element.frameContent : [])
const visiblePosts = computed(() => posts.value.slice(0, MAX_CANVAS_ITEMS))
const hiddenPostsCount = computed(() => Math.max(posts.value.length - visiblePosts.value.length, 0))
const sectionComponent = computed(() => String(editorPageStore.activePage?.content?.[props.sectionIndex]?.component ?? '').toLowerCase())
const previewDevice = computed(() => betaStore.previewDevice)
const previewVariant = computed<'cards' | 'carousel' | 'masonry'>(() => {
    const component = sectionComponent.value

    if (component.includes('apollo-one')) return 'carousel'
    if (component.includes('fleximages') || component.includes('galerias')) return 'masonry'

    return 'cards'
})

const stripHtml = (value?: string) => (typeof value === 'string' ? value.replace(/<[^>]*>/g, '').trim() : '')
const focalPoint = (post: any) => post?.mainImageExtra?.background_image_focal_point ?? { x: 50, y: 50 }
const getPostImage = (post: any) => post?.mainImage ?? post?.image?.url ?? post?.image ?? ''

const formatDate = (date?: string) => {
    if (!date) return ''

    const parsed = new Date(`${date}T00:00:00`)

    if (Number.isNaN(parsed.getTime())) return ''

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(parsed)
}

const getAspectRatio = (post: any, fallback = '1.45 / 1') => {
    const width = Number(post?.width ?? post?.mainImageWidth ?? 0)
    const height = Number(post?.height ?? post?.mainImageHeight ?? 0)

    if (width > 0 && height > 0) return `${width} / ${height}`

    return fallback
}

const getCardMediaStyle = (post: any, index: number) => {
    if (previewVariant.value === 'carousel') {
        return { aspectRatio: '16 / 10' }
    }

    if (previewVariant.value === 'masonry') {
        return { aspectRatio: getAspectRatio(post, index % 3 === 0 ? '4 / 5' : '4 / 3') }
    }

    return { aspectRatio: '1.45 / 1' }
}

const getCardStyle = (post: any, index: number) => {
    if (previewVariant.value !== 'masonry') return undefined

    const width = Number(post?.width ?? 0)
    const height = Number(post?.height ?? 0)
    const isWide = width > 0 && height > 0 && width / height > 1.2

    if (isWide && index % 5 === 0) {
        return { gridColumn: 'span 2' }
    }

    return undefined
}

const getSummary = (post: any) => {
    if (typeof post?.summary !== 'string') return ''

    return stripHtml(post.summary)
}
</script>

<template>
    <div
        v-if="posts.length > 0"
        :data-preview-device="previewDevice"
        :class="[
            'editor-subpages',
            `editor-subpages--${previewVariant}`,
        ]"
    >
        <article
            v-for="(post, index) in visiblePosts"
            :key="post.postId ?? index"
            class="editor-subpages__card"
            :style="getCardStyle(post, index)"
        >
            <div class="editor-subpages__image" :style="getCardMediaStyle(post, index)">
                <beta-base-image
                    v-if="getPostImage(post)"
                    :src="getPostImage(post)"
                    class-name="editor-subpages__image-file"
                    :style="{
                        objectPosition: `${focalPoint(post).x}% ${focalPoint(post).y}%`,
                    }"
                />

                <div v-else class="editor-subpages__image-placeholder">
                    Sem capa
                </div>
            </div>

            <div class="editor-subpages__content">
                <p v-if="formatDate(post.display_date)" class="editor-subpages__date">
                    {{ formatDate(post.display_date) }}
                </p>

                <h3 class="editor-subpages__title">
                    {{ stripHtml(post.title) }}
                </h3>

                <p v-if="previewVariant === 'carousel' && getSummary(post)" class="editor-subpages__summary">
                    {{ getSummary(post) }}
                </p>
            </div>
        </article>

        <div v-if="hiddenPostsCount > 0" class="editor-subpages__more">
            +{{ hiddenPostsCount }}
        </div>
    </div>
</template>

<style scoped>
.editor-subpages {
    width: 100%;
}

.editor-subpages--cards,
.editor-subpages--masonry {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
}

.editor-subpages--carousel {
    display: grid;
    grid-auto-columns: minmax(260px, 36%);
    grid-auto-flow: column;
    gap: 20px;
    overflow: hidden;
    padding-bottom: 6px;
    scroll-snap-type: x proximity;
}

.editor-subpages__card {
    overflow: hidden;
    border-radius: 24px;
    background: #fff;
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
    min-width: 0;
}

.editor-subpages--carousel .editor-subpages__card {
    scroll-snap-align: start;
}

.editor-subpages--masonry {
    grid-auto-flow: dense;
}

.editor-subpages__image {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: #ececec;
}

.editor-subpages__image-file {
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
}

.editor-subpages__image-placeholder {
    display: flex;
    height: 100%;
    min-height: 180px;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 14px;
    font-weight: 700;
}

.editor-subpages__content {
    padding: 18px 20px 22px;
}

.editor-subpages__date {
    margin: 0 0 10px;
    color: #64748b;
    font-size: 13px;
    line-height: 1.2;
}

.editor-subpages__title {
    margin: 0;
    color: #111827;
    font-size: 22px;
    font-weight: 800;
    line-height: 1.1;
    word-break: break-word;
}

.editor-subpages__summary {
    margin: 12px 0 0;
    color: #475569;
    font-size: 14px;
    line-height: 1.5;
}

.editor-subpages__more {
    display: flex;
    min-height: 160px;
    align-items: center;
    justify-content: center;
    border: 1px dashed #cbd5e1;
    border-radius: 24px;
    color: #64748b;
    font-size: 28px;
    font-weight: 800;
}

@media (max-width: 1024px) {
    .editor-subpages--cards,
    .editor-subpages--masonry {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 768px) {
    .editor-subpages--cards,
    .editor-subpages--masonry {
        grid-template-columns: minmax(0, 1fr);
    }

    .editor-subpages--carousel {
        grid-auto-columns: minmax(240px, 82%);
    }
}

.editor-subpages[data-preview-device='mobile'].editor-subpages--cards,
.editor-subpages[data-preview-device='mobile'].editor-subpages--masonry {
    grid-template-columns: minmax(0, 1fr);
}

.editor-subpages[data-preview-device='mobile'].editor-subpages--carousel {
    grid-auto-columns: minmax(220px, 86%);
}
</style>
